export const degreesToRadians = (degreeValue) => (degreeValue * (Math.PI / 180));
export const radiansToDegrees = (radianValue) => (radianValue * (180 / Math.PI));

/** Return a new point after rotating the current point around an axis. */
export const rotatePointAroundAxis = (pointCoordinates, rotationAxis, rotationDegrees) => {
    const cosineOfDegrees = (degreeValue) => (Math.cos(degreesToRadians(degreeValue)));
    const sineOfDegrees = (degreeValue) => (Math.sin(degreesToRadians(degreeValue)));
    const axisX = rotationAxis[0];
    const axisY = rotationAxis[1];
    const axisZ = rotationAxis[2];
    const requestedDegrees = rotationDegrees;

    const rotatedX = pointCoordinates[0] * (cosineOfDegrees(requestedDegrees) + axisX * axisX * (1 - cosineOfDegrees(requestedDegrees))) +
        pointCoordinates[1] * (axisX * axisY * (1 - cosineOfDegrees(requestedDegrees)) - axisZ * sineOfDegrees(requestedDegrees)) +
        pointCoordinates[2] * (axisX * axisZ * (1 - cosineOfDegrees(requestedDegrees)) + axisY * sineOfDegrees(requestedDegrees));

    const rotatedY = pointCoordinates[0] * (axisY * axisX * (1 - cosineOfDegrees(requestedDegrees)) + axisZ * sineOfDegrees(requestedDegrees)) +
        pointCoordinates[1] * (cosineOfDegrees(requestedDegrees) + axisY * axisY * (1 - cosineOfDegrees(requestedDegrees))) +
        pointCoordinates[2] * (axisY * axisZ * (1 - cosineOfDegrees(requestedDegrees)) - axisX * sineOfDegrees(requestedDegrees));

    const rotatedZ = pointCoordinates[0] * (axisZ * axisX * (1 - cosineOfDegrees(requestedDegrees)) - axisY * sineOfDegrees(requestedDegrees)) +
        pointCoordinates[1] * (axisZ * axisY * (1 - cosineOfDegrees(requestedDegrees)) + axisX * sineOfDegrees(requestedDegrees)) +
        pointCoordinates[2] * (cosineOfDegrees(requestedDegrees) + axisZ * axisZ * (1 - cosineOfDegrees(requestedDegrees)));

    return [rotatedX, rotatedY, rotatedZ];
};

/** Return the combined axis and angle produced by two rotations. */
export const combineAxisAngleRotations = (addedRotationDegrees, addedRotationAxis, existingRotationAxis, existingRotationDegrees) => {
    const cosineOfDegrees = (degreeValue) => (Math.cos(degreesToRadians(degreeValue)));
    const sineOfDegrees = (degreeValue) => (Math.sin(degreesToRadians(degreeValue)));

    const addedAxisX = addedRotationAxis[0];
    const addedAxisY = addedRotationAxis[1];
    const addedAxisZ = addedRotationAxis[2];

    const existingAxisX = existingRotationAxis[0];
    const existingAxisY = existingRotationAxis[1];
    const existingAxisZ = existingRotationAxis[2];

    const halfAngleCosine = cosineOfDegrees(addedRotationDegrees / 2) * cosineOfDegrees(existingRotationDegrees / 2) -
        sineOfDegrees(addedRotationDegrees / 2) * sineOfDegrees(existingRotationDegrees / 2) *
        (addedAxisX * existingAxisX + addedAxisY * existingAxisY + addedAxisZ * existingAxisZ);

    const normalizedHalfAngleCosine = Math.abs(halfAngleCosine) > 1 ?
        halfAngleCosine / Math.abs(halfAngleCosine) : halfAngleCosine;
    const combinedRotationDegrees = 2 * radiansToDegrees(Math.acos(normalizedHalfAngleCosine));

    const combinedAxisX = ((
        sineOfDegrees(addedRotationDegrees / 2) * cosineOfDegrees(existingRotationDegrees / 2) * addedAxisX +
        cosineOfDegrees(addedRotationDegrees / 2) * sineOfDegrees(existingRotationDegrees / 2) * existingAxisX +
        sineOfDegrees(addedRotationDegrees / 2) * sineOfDegrees(existingRotationDegrees / 2) *
        (addedAxisY * existingAxisZ - addedAxisZ * existingAxisY)
    ) / sineOfDegrees(combinedRotationDegrees / 2));

    const combinedAxisY = ((
        sineOfDegrees(addedRotationDegrees / 2) * cosineOfDegrees(existingRotationDegrees / 2) * addedAxisY +
        cosineOfDegrees(addedRotationDegrees / 2) * sineOfDegrees(existingRotationDegrees / 2) * existingAxisY +
        sineOfDegrees(addedRotationDegrees / 2) * sineOfDegrees(existingRotationDegrees / 2) *
        (addedAxisZ * existingAxisX - addedAxisX * existingAxisZ)
    ) / sineOfDegrees(combinedRotationDegrees / 2));

    const combinedAxisZ = ((
        sineOfDegrees(addedRotationDegrees / 2) * cosineOfDegrees(existingRotationDegrees / 2) * addedAxisZ +
        cosineOfDegrees(addedRotationDegrees / 2) * sineOfDegrees(existingRotationDegrees / 2) * existingAxisZ +
        sineOfDegrees(addedRotationDegrees / 2) * sineOfDegrees(existingRotationDegrees / 2) *
        (addedAxisX * existingAxisY - addedAxisY * existingAxisX)
    ) / sineOfDegrees(combinedRotationDegrees / 2));

    return {
        rotationDegrees: combinedRotationDegrees,
        rotationAxis: [
            isNaN(combinedAxisX) || !isFinite(combinedAxisX) ? 1 : combinedAxisX,
            isNaN(combinedAxisY) || !isFinite(combinedAxisX) ? 1 : combinedAxisY,
            isNaN(combinedAxisZ) || !isFinite(combinedAxisX) ? 1 : combinedAxisZ
        ]
    };
};

export const measureProjectedMoveGap = (candidateCoordinates, currentCoordinates, horizontalMovement, verticalMovement) => {
    const horizontalGap = (currentCoordinates[0] + horizontalMovement) - candidateCoordinates[0];
    const verticalGap = (currentCoordinates[1] + verticalMovement) - candidateCoordinates[1];
    return Math.sqrt(horizontalGap * horizontalGap + verticalGap * verticalGap);
};

export const readPointerCoordinates = (pointerEvent) => {
    if (pointerEvent.targetTouches) {
        const primaryTouch = pointerEvent.targetTouches[0];
        return { clientX: primaryTouch.clientX, clientY: primaryTouch.clientY };
    }

    return { clientX: pointerEvent.clientX, clientY: pointerEvent.clientY };
};
